import { useTranslation } from 'react-i18next';
import { PLAN_MONTHS, formatPrice, parseTrial } from './utils';
import * as S from './styled';

interface DisclaimerParams {
  [key: string]: string | number | undefined;
}

interface PlanDetails {
  price: number;
  periodPrice: number;
  description: string;
  duration: string;
  currency?: string;
  currencySymbol?: string;
}

export interface SubscriptionDisclaimerProps {
  planDetails: PlanDetails | null;
  couponPrice: number;
  onLinkClick: (type: string) => void;
}

export const SubscriptionDisclaimer = ({
  planDetails,
  couponPrice,
  onLinkClick,
}: SubscriptionDisclaimerProps) => {
  const { t } = useTranslation();

  if (!planDetails?.description) return null;

  const { price, periodPrice, description, duration, currency, currencySymbol } = planDetails;
  const introPrice = +(periodPrice - couponPrice).toFixed(2);
  const planMonths = PLAN_MONTHS[description];
  const trial = parseTrial(duration);
  const formatMoney = (value: number | undefined) => formatPrice(value, currency, currencySymbol);

  // The legal copy below covers trials of a known length that renew monthly, and 1/3/6-month plans.
  const isCoveredPlan = trial
    ? trial.days > 0 && trial.renewsMonthly && planMonths === 1
    : Boolean(planMonths);

  let disclaimerKey: string;
  let disclaimerParams: DisclaimerParams;
  if (!isCoveredPlan) {
    disclaimerKey = 'trial_disclaimer';
    disclaimerParams = {
      periodPrice: formatMoney(introPrice),
      price: formatMoney(price),
      duration: t(description),
    };
  } else if (trial) {
    disclaimerKey = 'disclaimer_trial';
    disclaimerParams = {
      introPrice: formatMoney(introPrice),
      trialDays: trial.days,
      fullPrice: formatMoney(price),
    };
  } else if (introPrice < price) {
    disclaimerKey = planMonths === 1 ? 'disclaimer_1_month' : 'disclaimer_n_months';
    disclaimerParams = {
      introPrice: formatMoney(introPrice),
      fullPrice: formatMoney(price),
      count: planMonths,
    };
  } else {
    // No coupon (e.g. once the timer expires): the first period is full price, so there's no discount to disclose.
    disclaimerKey =
      planMonths === 1 ? 'disclaimer_1_month_full_price' : 'disclaimer_n_months_full_price';
    disclaimerParams = {
      fullPrice: formatMoney(price),
      count: planMonths,
    };
  }

  return (
    <S.Disclaimer
      dangerouslySetInnerHTML={{ __html: t(disclaimerKey, disclaimerParams) }}
      onClick={(e) => onLinkClick((e.target as HTMLElement)?.id)}
    />
  );
};
